// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import { toast } from 'sonner';
import { getBootstrapStructure } from '@staty/shared/utils/sportBootstrap';
import { bootstrapCompetitionName } from '@staty/shared/utils/competitionNaming';
import { browserTimeZone } from '@/utils/registrationDateTz';
import useOnboardingStore from '@/store/onboardingStore';
import { cleanStructureNames } from '@/components/CreateOrganization/StructureNamesEditor';
import WhoIsPlayingChoice, { type WhoIsPlayingMode } from '@/components/CreateOrganization/WhoIsPlayingChoice';
import PendingDescriptionBanner, { readPendingSport, hasPendingCarry } from '@/components/CreateOrganization/PendingDescriptionBanner';
import { ONBOARDING_SUPPORTED_SPORTS } from '@staty/shared/utils/onboardingTemplates';

import useOrganizationStore from '@store/organizationStore';
import { useUser, useAuth, useOrganizationList } from '@/auth/clerkShim';
import { useNavigate, useSearchParams } from "react-router-dom";
import _ from 'lodash';
import { isSlugBlacklisted } from '@staty/shared/blacklistedSlugs';
import CreateOnboardingShell from './CreateOnboardingShell';
import WizardStepPill from './WizardStepPill';
import { sportOptions, sportPlaceholders } from '@/components/CreateOrganization/sportOptions';
import { useSlugAvailability } from '@/components/CreateOrganization/useSlugAvailability';
import { TENANT_ROOT_DOMAIN } from '@staty/shared/domains';
import { trackEvent } from '@/utils/analytics';

export default function CreateLeaguePage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const { setActive, userMemberships } = useOrganizationList();

  const { createOrganization } = useOrganizationStore((state) => ({
    createOrganization: state.createOrganization,
  }));

  const [nameError, setNameError] = useState("");
  const [urlError, setUrlError] = useState("");
  // INT-0011: preselect the sport the visitor picked on the landing
  // AI-teaser (peek only — Get Started owns cleanup of the pending keys).
  const [selectedSport, setSelectedSport] = useState(() => {
    const pending = readPendingSport();
    return sportOptions.some((o) => o.value === pending) ? pending : "basketball";
  });

  const {
    leagueName,
    setLeagueName,
    customSlug,
    setCustomSlug,
    handleSlugChange: hookHandleSlugChange,
    leagueUrl,
    finalSlug,
    slugStatus,
    slugSuggestions,
    selectSuggestion: hookSelectSuggestion,
  } = useSlugAvailability();

  const isTournamentFlow = searchParams.get("tournament") === "1";
  // URLSearchParams decodes once; step 1 no longer double-encodes (INT-0038).
  const locationParam = searchParams.get("location") || '';

  // INT-0011 tier 2: a carried teaser description/structure adds the
  // "Confirm your structure" step — only for sports the onboarding builder
  // supports. Decided here (not at the confirm page) because it also skips
  // the bootstrap SAMPLE competition: the built structure replaces it.
  const goesToConfirmStep = hasPendingCarry() && ONBOARDING_SUPPORTED_SPORTS.includes(selectedSport);

  // INT-0039: ordinary signups preview + rename the structure the org will
  // start with (the bootstrap's names), then Continue creates the org with
  // the sample competition SKIPPED and builds the edited draft through
  // onboarding/build (one path, same as the carried-teaser confirm step).
  const buildStructure = useOnboardingStore((s) => s.buildStructure(getToken));
  const contestType = isTournamentFlow ? 'tournament' : 'season';
  const seedStructure = (sport) => {
    // League and tournament defaults differ per sport (ADR 0067). First
    // listed = highest priority (what the build endpoint and the bootstrap's
    // example-team seeding both treat as "first").
    const tpl = getBootstrapStructure(sport, contestType);
    const divisions = [...tpl.divisions].sort((a, b) => b.priority - a.priority).map((d) => ({ name: d.name }));
    return {
      name: bootstrapCompetitionName(contestType),
      // Each contest owns its division list (owner 2026-10-16); seeded alike.
      contests: isTournamentFlow ? tpl.contests.map((name) => ({ name, divisions: divisions.map((d) => ({ ...d })) })) : [],
      divisions,
    };
  };
  const [structure, setStructure] = useState(() => seedStructure(selectedSport));
  // Nothing edits the seeded structure on this step any more (INT-0051); the
  // dirty flags stay false so a sport switch always re-seeds.
  const [structureDirty] = useState({ name: false, contests: false, divisions: false });
  // Sport switch re-seeds the lists the organizer has NOT touched.
  useEffect(() => {
    const seeded = seedStructure(selectedSport);
    setStructure((cur) => ({
      name: structureDirty.name ? cur.name : seeded.name,
      contests: structureDirty.contests ? cur.contests : seeded.contests,
      divisions: structureDirty.divisions ? cur.divisions : seeded.divisions,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSport]);
  // INT-0051 (owner 2026-10-21): the inline "Your first competition" panel is
  // gone from this step; the sport template's defaults are built silently on
  // Continue and shown for editing on the next step, "Your divisions"
  // (owner 2026-10-22). In its place: the Who's playing? choice, carried
  // through /create/divisions?mode= to /create/add-teams. Ordinary signups
  // only (carried-teaser signups confirm their structure instead).
  const showStructure = !goesToConfirmStep;

  // ── Org-findability guards (funnel finding 2026-09-14): three organizers
  // created 3-4 duplicate orgs EACH, same day (Dromana/EHBL/Cariri) — people
  // re-run this wizard instead of opening the org they already have. Guard 1:
  // a can't-miss banner when the signed-in admin already owns orgs. Guard 2:
  // a warning when the typed name matches one of their own orgs.
  const existingOrgs = ((userMemberships && userMemberships.data) || [])
    .map((m) => m.organization)
    .filter((o) => o && o.id && o.name);
  const [whoChoice, setWhoChoice] = useState<WhoIsPlayingMode>('bring');
  // Owner 2026-10-26: "I have a schedule" skips "Your divisions" and opens the
  // upload page; the file decides the divisions, contests, and venues. Only
  // "Draft one for me" needs the divisions (and venues) step first.
  const nextStepUrl = (choice: WhoIsPlayingMode) => (
    choice === 'bring' ? '/create/add-teams?mode=bring' : `/create/divisions?mode=${choice}`
  );
  const [dismissedExisting, setDismissedExisting] = useState(false);
  // Owner 2026-10-21: an existing org is SELECTABLE (the first one by
  // default). While one is selected the sport/name/URL fields are hidden and
  // not required; Continue activates that org and carries the Who's playing?
  // choice to the next step. "I need a separate organization" clears it.
  const [selectedExistingId, setSelectedExistingId] = useState<string | null>(null);
  useEffect(() => {
    if (!dismissedExisting && existingOrgs.length > 0 && selectedExistingId === null) {
      setSelectedExistingId(existingOrgs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingOrgs.length, dismissedExisting]);
  const selectedExisting = !dismissedExisting && selectedExistingId
    ? existingOrgs.find((o) => o.id === selectedExistingId) || null
    : null;
  const usingExisting = !!selectedExisting && !goesToConfirmStep;
  const continueWithExisting = async () => {
    if (!selectedExisting || loading) return;
    setLoading(true);
    try {
      await setActive({ organization: selectedExisting.id });
    } catch (e) {
      console.error('Failed to switch to the existing organization', e);
    }
    trackEvent('wizard_existing_org_continued', { who_choice: whoChoice });
    navigate(nextStepUrl(whoChoice));
  };
  const normalizeName = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const typedNorm = normalizeName(leagueName);
  const similarOrg = typedNorm.length >= 4
    ? existingOrgs.find((o) => {
        const en = normalizeName(o.name);
        const es = normalizeName(o.slug);
        return en === typedNorm || es === typedNorm
          || (typedNorm.length >= 5 && (en.includes(typedNorm) || typedNorm.includes(en)));
      }) || null
    : null;
  const openExistingOrg = async (org) => {
    try {
      await setActive({ organization: org.id });
    } catch (e) {
      // Non-fatal: /home resolves the active org either way.
      console.error('Failed to switch to the existing organization', e);
    }
    navigate('/admin/home');
  };

  const handleSlugChange = (value: string) => {
    hookHandleSlugChange(value);
    setUrlError("");
  };

  const selectSuggestion = (suggestion: string) => {
    hookSelectSuggestion(suggestion);
    setUrlError("");
  };

  const canSubmit = usingExisting || (
    leagueName.trim() &&
    finalSlug &&
    finalSlug.length >= 3 &&
    slugStatus === 'available');

  const onSubmit = async () => {
    if (usingExisting) return continueWithExisting();
    setNameError("");
    setUrlError("");

    if (!leagueName.trim()) {
      setNameError('Please enter a name for your organization');
      return;
    }

    if (!finalSlug || finalSlug.length < 3) {
      setUrlError("URL must be at least 3 characters");
      return;
    }

    if (slugStatus === 'checking') {
      setUrlError("Please wait while we check availability");
      return;
    }

    if (slugStatus === 'taken') {
      setUrlError("This URL is already taken. Please choose a different one or select a suggestion below.");
      return;
    }

    if (isSlugBlacklisted(finalSlug)) {
      setUrlError("This URL is not available. Please try another.");
      return;
    }

    const token = await getToken();
    setLoading(true);
    
    createOrganization(leagueName, finalSlug, user.id, token, null, async (res, err) => {
      setLoading(false);
      if (err || !res) {
        setUrlError(err || "Something went wrong. Please try again.");
        return;
      }
      // The API returns { organization, admin } — the new org's id is the
      // reliable source. `admin.provider.organization` is a Clerk-era field
      // that is empty for Mongo-JWT admins; reading it alone sent
      // switch-org an undefined id → 403 "Not a member of that organization".
      const newOrg = _.get(res, 'organization._id') || _.get(res, 'admin.provider.organization');
      trackEvent('org_created', {
        sport: selectedSport,
        flow: isTournamentFlow ? 'tournament' : 'league',
        who_choice: goesToConfirmStep ? undefined : whoChoice,
      });
      if (newOrg) {
        // MUST await: setActive POSTs /admins/auth/switch-org, and the page we
        // navigate to re-reads /admins/auth/me on mount — navigating first
        // races that read against the switch.
        try {
          await setActive({ organization: newOrg });
        } catch (e) {
          // Non-fatal: the org exists either way, so let the user through.
          console.error('Failed to activate the new organization', e);
        }
      }
      if (goesToConfirmStep) {
        navigate(`/create/confirm-structure?sport=${selectedSport}${isTournamentFlow ? '&tournament=1' : ''}`);
        return;
      }
      // INT-0039: build the previewed structure (+ its open registration,
      // ADR 0037) before the design step. The org exists either way, so a
      // build failure warns and continues; Get Started's attach path remains.
      if (newOrg) {
        // A draft collapsed while invalid falls back to the cleaned names;
        // cleanStructureNames drops empties, and the server's sanitizer
        // supplies "Open Division" when nothing survives.
        const clean = cleanStructureNames(structure);
        // Tournament: every contest carries its own divisions (the server
        // falls back to the top-level list for any contest sent as a bare
        // string, so the confirm step and Get Started keep working).
        const divisionCount = contestType === 'tournament'
          ? clean.contests.reduce((n, c) => n + c.divisions.length, 0)
          : clean.divisions.length;
        try {
          await buildStructure(newOrg, {
            name: clean.name,
            contestType,
            contests: clean.contests.map((c) => ({ name: c.name, divisions: c.divisions.map((d) => ({ name: d.name })) })),
            divisions: (contestType === 'tournament' ? (clean.contests[0]?.divisions || []) : clean.divisions).map((d) => ({ name: d.name })),
            seedExampleTeams: true,
            registration: { open: true, timeZone: browserTimeZone() },
          });
          trackEvent('org_structure_built', { sport: selectedSport, flow: contestType, divisions: divisionCount });
        } catch (e) {
          console.error('Inline structure build failed', e);
          toast.error('Your organization was created, but the competition could not be built. You can add it from Get Started.');
        }
      }
      // Next: "I have a schedule" goes straight to the upload page (the
      // divisions come from the file); "Draft one for me" first shows "Your
      // divisions" (the tree just built, editable). See nextStepUrl.
      navigate(nextStepUrl(whoChoice));
      // skipSampleCompetition: the inline panel's build replaces the bootstrap
      // sample competition for ordinary signups too (INT-0039).
    }, [selectedSport], isTournamentFlow ? 1 : 0, locationParam, 1);
  };

  return (
    <CreateOnboardingShell>
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-16 pt-4">
        <div className="w-full max-w-[540px]">
          {/* Hero text */}
          <div className="mb-10 text-center">
            <WizardStepPill step={2} />
            <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              {usingExisting ? 'Your organization' : 'Name your organization'}
            </h1>
            <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              {usingExisting
                ? 'Continue with your existing organization, or create a separate one.'
                : isTournamentFlow
                  ? "Choose a sport and name that represents your organization"
                  : "Pick your sport and name your organization. Your league will live inside it"
              }
            </p>
          </div>

          {/* Guard 1: the admin already owns organizations — put them one
              click from OPENING one instead of duplicating it. */}
          {existingOrgs.length > 0 && !dismissedExisting && (
            <div className="mb-6 rounded-2xl bg-primary/5 p-4 ring-1 ring-primary/25">
              <div className="flex items-start gap-3">
                <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="min-w-0 flex-1 text-sm">
                  <p className="font-semibold text-foreground">
                    {existingOrgs.length === 1
                      ? <>You already have <span className="text-primary">{existingOrgs[0].name}</span>.</>
                      : <>You already have {existingOrgs.length} organizations.</>}
                  </p>
                  <p className="mt-0.5 leading-relaxed text-muted-foreground">
                    A new organization is a separate site with its own URL, teams, and
                    registrations. New seasons, leagues, and tournaments belong inside
                    the organization you have.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Your organizations">
                    {existingOrgs.slice(0, 3).map((o, i) => {
                      const selected = selectedExistingId === o.id;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => setSelectedExistingId(o.id)}
                          aria-pressed={selected}
                          data-tour={`create-existing-org-${i}`}
                          className={`rounded-xl px-4 py-2 text-xs font-semibold transition-opacity ${
                            selected ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground ring-1 ring-primary/40 hover:bg-primary/10'
                          }`}
                        >
                          {selected ? '\u2713 ' : ''}{o.name}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => { setDismissedExisting(true); setSelectedExistingId(null); }}
                      data-tour="create-existing-separate"
                      className="rounded-xl px-4 py-2 text-xs font-medium text-muted-foreground ring-1 ring-border transition-colors hover:bg-muted/50"
                    >
                      I need a separate organization
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* INT-0011: landing carry-through stays visible here too. */}
          <PendingDescriptionBanner />

          {/* Main card */}
          <div className="rounded-[28px] bg-card p-8 shadow-2xl ring-1 ring-border">
            {!usingExisting && (<>
            {/* Sport Selection */}
            <div className="mb-6">
              <label className="mb-3 block text-sm font-semibold text-foreground">
                Select your sport
              </label>
              <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-4">
                {sportOptions.map((sport) => (
                  <button
                    key={sport.value}
                    type="button"
                    onClick={() => setSelectedSport(sport.value)}
                    className={`group relative flex flex-col items-center gap-1.5 rounded-2xl p-3 transition-all duration-200 ${
                      selectedSport === sport.value
                        ? 'bg-gradient-to-br ' + sport.color + ' text-white shadow-lg scale-[1.02]'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:scale-[1.02]'
                    }`}
                  >
                    <span className="text-2xl">{sport.icon}</span>
                    <span className={`text-[10px] font-medium ${selectedSport === sport.value ? 'text-white/90' : 'text-muted-foreground'}`}>
                      {sport.label}
                    </span>
                    {selectedSport === sport.value && (
                      <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm">
                        <svg className="h-3 w-3 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="my-6 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

            {/* Name Input */}
            <div className="mb-5">
              <label className="mb-2 block text-sm font-semibold text-foreground">
                Organization name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={leagueName}
                  onChange={(e) => {
                    setLeagueName(e.target.value);
                    setNameError("");
                    setUrlError("");
                  }}
                  className={`w-full rounded-2xl border-0 bg-muted/50 px-5 py-4 text-base text-foreground ring-1 transition-all duration-200 placeholder:text-muted-foreground/60 focus:bg-background focus:outline-none focus:ring-2 ${
                    nameError ? 'ring-destructive/40 focus:ring-destructive' : 'ring-border focus:ring-ring'
                  }`}
                  placeholder={sportPlaceholders[selectedSport]}
                />
                {leagueName && (
                  <button
                    type="button"
                    onClick={() => {
                      setLeagueName("");
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-muted-foreground"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {nameError && (
                <p className="mt-2 flex items-center gap-1.5 text-sm text-destructive">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {nameError}
                </p>
              )}
              {/* Guard 2: the typed name matches an org they ALREADY own. */}
              {!nameError && similarOrg && (
                <div className="mt-2 flex items-start gap-2.5 rounded-xl bg-warning/10 px-3.5 py-2.5 text-sm ring-1 ring-warning/30">
                  <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                  </svg>
                  <span className="text-foreground">
                    This looks like <b>{similarOrg.name}</b>, which you already have:{' '}
                    <button
                      type="button"
                      onClick={() => openExistingOrg(similarOrg)}
                      className="font-semibold text-primary underline underline-offset-2 hover:opacity-80"
                    >
                      open it instead
                    </button>. A second organization would be a separate site.
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Made a typo? You can change your organization&apos;s name and URL any time in Settings.
                  </span>
                </div>
              )}
            </div>

            {/* URL Preview - Editable */}
            {(leagueUrl || customSlug) && (
              <div className={`mb-5 overflow-hidden rounded-2xl bg-gradient-to-br from-muted/50 to-muted/70 p-4 ring-1 ${
                slugStatus === 'taken' ? 'ring-destructive/30' : slugStatus === 'available' ? 'ring-success/30' : 'ring-border/60'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground/60">Your public URL</p>
                    <div className="mt-1.5 flex items-center font-mono text-sm">
                      <input
                        type="text"
                        value={customSlug || leagueUrl}
                        onChange={(e) => handleSlugChange(e.target.value)}
                        className={`w-full min-w-0 flex-1 bg-transparent font-semibold outline-none ${
                          slugStatus === 'taken' ? 'text-destructive' : 'text-success'
                        }`}
                        placeholder="your-league-name"
                      />
                      <span className="flex-shrink-0 text-muted-foreground">.{TENANT_ROOT_DOMAIN}</span>
                    </div>
                  </div>
                  <div className={`ml-3 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${
                    slugStatus === 'checking' ? 'bg-muted text-muted-foreground/60' :
                    slugStatus === 'available' ? 'bg-success/10 text-success' :
                    slugStatus === 'taken' ? 'bg-destructive/10 text-destructive' :
                    'bg-muted text-muted-foreground/60'
                  }`}>
                    {slugStatus === 'checking' ? (
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : slugStatus === 'available' ? (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : slugStatus === 'taken' ? (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Suggestions */}
                {slugStatus === 'taken' && slugSuggestions.length > 0 && (
                  <div className="mt-3 border-t border-border/60 pt-3">
                    <p className="mb-2 text-xs font-medium text-muted-foreground">Try one of these instead:</p>
                    <div className="flex flex-wrap gap-2">
                      {slugSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => selectSuggestion(suggestion)}
                          className="rounded-lg bg-card px-3 py-1.5 font-mono text-xs font-medium text-foreground ring-1 ring-border transition-all hover:bg-muted/50 hover:ring-input"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            </>)}

            {/* INT-0051: how the schedule gets onto the site; the default
                competition itself is built silently on Continue. */}
            {showStructure && (
              <div className="mb-5" data-tour="create-who-choice">
                <p className="mb-3 text-sm font-medium text-foreground">Who&apos;s playing?</p>
                <WhoIsPlayingChoice value={whoChoice} onSelect={setWhoChoice} disabled={loading} />
                <p className="mt-3 text-xs text-muted-foreground">
                  {usingExisting
                    ? <>Your {isTournamentFlow ? 'tournament' : 'league'} goes into <b>{selectedExisting!.name}</b>. You can rename or change its divisions on the next step.</>
                    : <>We set up your first {isTournamentFlow ? 'tournament' : 'league'} with the usual divisions for your sport. You can rename or change them on the next step.</>}
                </p>
              </div>
            )}

            {/* Error Message */}
            {urlError && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl bg-destructive/10 px-4 py-3.5 text-sm text-destructive ring-1 ring-destructive/30">
                <svg className="mt-0.5 h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{urlError}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="button"
              onClick={onSubmit}
              disabled={loading || !canSubmit}
              className="group relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-primary to-primary-deep px-6 py-4 text-base font-semibold text-primary-foreground shadow-xl shadow-primary/25 transition-all duration-300 hover:from-primary/90 hover:to-primary-deep/90 hover:shadow-2xl hover:shadow-primary/30 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="relative z-10 flex items-center justify-center gap-2 text-white">
                {loading ? (
                  <>
                    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {usingExisting ? `Opening ${selectedExisting!.name}...` : 'Creating your organization and first competition...'}
                  </>
                ) : (
                  <>
                    Continue
                    <svg className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </>
                )}
              </span>
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            </button>
          </div>

          {/* Trust badges */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground/60">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-success/10 ring-1 ring-success/20">
                <svg className="h-4 w-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span>Free forever plan</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
                <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span>Ready in 2 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
                <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <span>No credit card needed</span>
            </div>
          </div>
        </div>
      </main>
    </CreateOnboardingShell>
  );
}
